import React, {
	ChangeEvent, ClipboardEvent, FC, KeyboardEvent, useEffect, useMemo,
} from 'react';

import {
	AsyncCellSearch,
	ICellEditor,
} from '@aleph/shared-components';
import {
	DeviceOptionsQuery, RedditDeviceLabelsQuery, RedditDeviceLabelsQueryVariables,
} from 'wb-reddit/graphql/generated';
import { uniqBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import redditDeviceLabelsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/deviceLabels.graphql';

const DevicesCellEditor: FC<ICellEditor<DeviceOptionsQuery['options']['items']>> = (
	params,
) => {
	const initialValue = useMemo(() => params.value, []);
	const {
		data, value, setValue, editorContext, setEditorContext,
	} = params;

	const [getDeviceLabels, { loading }] = useLazyQuery<RedditDeviceLabelsQuery, RedditDeviceLabelsQueryVariables>(redditDeviceLabelsQuery);

	const changeSearch = (e: ChangeEvent<HTMLInputElement>) => {
		setEditorContext({
			...editorContext,
			search: e.target.value,
		});
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			params.stopEditing();
		}

		if (e.key === 'Escape') {
			setValue(initialValue);

			setTimeout(() => params.stopEditing());
		}
	};

	useEffect(() => {
		setEditorContext({
			...editorContext,
			deviceType:  data?.deviceType,
			adAccountId: data?.parent?.parent?.adAccount?.adAccountId,
		});
	}, [data]);

	const onPaste = async (e: ClipboardEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const deviceIds = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		try {
			const { data: deviceData } = await getDeviceLabels({
				variables: {
					deviceIds,
				},
			});

			setValue(
				uniqBy(
					[
						...(value ?? []),
						...(deviceData?.targetingDevices?.items ?? []),
					] as DeviceOptionsQuery['options']['items'],
					'value',
				),
			);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<AsyncCellSearch onChange={changeSearch}
			onKeyDown={onKeyDown}
			onPaste={onPaste}
			value={editorContext?.search ?? ''}
			loading={loading}
			loaderText="Pasting..."
			placeholder='Search devices...'
			onSubmit={() => params.stopEditing()}
			onCancel={() => params.api.stopEditing(true)}
		/>
	);
};

export default DevicesCellEditor;
